import { Injectable, signal, computed, Signal } from '@angular/core';
import { WorkflowClass } from '@modules/campaigns/Domain/WorkflowClass';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';
import { BehaviorSubject } from 'rxjs';
import { NodeValidationError } from '@modules/campaigns/Domain/Interfaces/NodeValidationError';

export interface EventNode {
  type: 'dragged' | 'existing' | 'future';
  nodeId: string;
  nodeData?: any;
}

@Injectable({ providedIn: 'root' })
export class WorkflowStateService {
  private _workflow = signal<WorkflowClass | null>(null);
  private _selectedNodeId = signal<string | null>(null);
  private _highlightedNodeId = signal<string | null>(null);

  //Computed signals consumed for service or UI
  readonly workflow: Signal<WorkflowClass | null> = computed(() => this._workflow());
  readonly nodes = computed(() => this._workflow()?.nodes ?? []);
  readonly selectedNodeId: Signal<string | null> = computed(() => this._selectedNodeId());
  readonly highlightedNodeId: Signal<string | null> = computed(() => this._highlightedNodeId());
  public selectedNodeEvent$ = new BehaviorSubject<EventNode | null>(null);

  readonly smsCount = computed(() => this._workflow()?.getSmsCount() ?? 0);
  readonly emailCount = computed(() => this._workflow()?.getEmailCount() ?? 0);

  loadWorkflow(workflow: WorkflowClass) {
    this._workflow.set(this.cloneWorkflow(workflow));
    this._selectedNodeId.set(null);
    this._highlightedNodeId.set(null);
  }

  setSelectedNode(event: EventNode | null) {
    if (!event) {
      this._selectedNodeId.set(null);
      this._highlightedNodeId.set(null);
      this.selectedNodeEvent$.next(null);
      return;
    }

    this._selectedNodeId.set(event.nodeId);
    this.selectedNodeEvent$.next(event);
  }

  setHighlightedNode(id: string | null) {
    this._highlightedNodeId.set(id);
  }

  getNodeSignal(id: string): Signal<NodeClass | null> {
    return computed(() => this._workflow()?.nodes.find((n) => n.id === id) ?? null);
  }

  addOrUpdateNode(node: NodeClass) {
    const wf = this._workflow();
    if (!wf) return;
    const clone = this.cloneWorkflow(wf);
    const idx = clone.nodes.findIndex((n) => n.id === node.id);
    if (idx === -1) {
      clone.nodes.push(node);
    } else {
      clone.nodes[idx] = node;
    }
    this._workflow.set(clone);
  }

  updateNodeConfig(nodeId: string, partialConfig: Record<string, any>) {
    const wf = this._workflow();
    if (!wf) return;
    const clone = this.cloneWorkflow(wf);
    const idx = clone.nodes.findIndex((n) => n.id === nodeId);
    if (idx === -1) return;
    const node = clone.nodes[idx];

    if ((node as any).cloneWithConfig && typeof (node as any).cloneWithConfig === 'function') {
      const updated = (node as any).cloneWithConfig(partialConfig);
      clone.nodes[idx] = updated;
    } else {
      node.config = { ...(node.config || {}), ...partialConfig };
      clone.nodes[idx] = node;
    }
    this._workflow.set(clone);
  }

  getAllValidationErrors(): {
    workflowErrors: string[];
    nodeErrors: NodeValidationError[];
  } {
    const wf = this._workflow();
    if (!wf) {
      return { workflowErrors: [], nodeErrors: [] };
    }

    return {
      workflowErrors: wf.validate?.() ?? [],
      nodeErrors: wf.validateNodes?.() ?? [],
    };
  }

  setWorkflowName(name: string) {
    const wf = this._workflow();
    if (!wf) return;
    const clone = this.cloneWorkflow(wf);
    clone.name = name;
    this._workflow.set(clone);
  }

  removeNode(nodeId: string) {
    const wf = this._workflow();
    if (!wf) return;
    const clone = this.cloneWorkflow(wf);
    clone.removeNode(nodeId);
    this._workflow.set(clone);
  }

  private cloneWorkflow(src: WorkflowClass): WorkflowClass {
    const clone = new WorkflowClass((src as any).id, (src as any).name, (src as any).type);
    clone.status = (src as any).status;
    clone.nodes = (src.nodes || []).slice();
    return clone;
  }
}
